import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getContentTreeForProject, deleteTask, exportProjectArticles, reassignTaskStream, createStream,
  previewStreamRename, renameStream as renameStreamApi, StreamRenameResult,
} from '../services/api';
import SchedulePublicationModal from './SchedulePublicationModal';
import CatalogueMenuBar from './CatalogueMenuBar';
import CatalogueLinkBadges, { LinkGraphCacheProvider } from './ContentBrowser/CatalogueLinkBadges';
import MasterplanPanel from './MasterplanPanel/MasterplanPanel';
import StreamRenameRow from './StreamRenameRow';
import StreamRenameConfirmModal from './StreamRenameConfirmModal';
import './ContentCatalogue.css';
import {
    ChevronDown,
    ChevronRight,
    Check,
    Clock,
    XCircle,
    Image as ImageIcon,
    BarChart3,
    Calendar,
    Trash2,
    BookOpen,
    RefreshCw,
    FileText,
    Share2,
    Search,
    Megaphone,
    Mail,
    CalendarDays,
    Lightbulb,
    X,
    ArrowRightLeft,
    Plus,
    Layers,
    Video,
    Quote,
    Key,
    Send,
    HelpCircle,
    Pencil,
} from 'lucide-react';

/** Strip a masterplan stream's numeric prefix ("1)\n" / "1) ") for display —
 * mirrors the backend's stream_utils numeric-prefix convention. Never used
 * for comparison/storage, display only. */
const stripStreamPrefix = (name: string): string => name.replace(/^\s*\d+\s*\)\s*/, '');

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

interface QuickWinCount {
  social_media: number;
  meta_data: number;
  ad_copy: number;
  email_newsletter: number;
  content_calendar: number;
  keyword_research: number;
  multichannel_carousel: number;
  multichannel_video_script: number;
  multichannel_pull_quotes: number;
}

interface ArticleNode {
  task_id: string;
  title: string;
  status: string;
  has_image: boolean;
  quick_wins: string[];
  approval_status?: string | null;
}

interface StreamNode {
  name: string;
  article_count: number;
  articles: ArticleNode[];
}

interface QuickWinGroup {
  task_id: string;
  title: string;
  source_article?: string;
  source_task_id?: string | null;
  source_hero_image_url?: string | null;
  approval_status?: string | null;
}

interface EDMTemplateNode {
  template_id: string;
  name: string;
  category: string;
  status: string;
  usage_count: number;
}

interface EDMSequenceNode {
  sequence_id: string;
  name: string;
  status: string;
  step_count: number;
}

interface ContentTreeData {
  overview: {
    articles: number;
    social_media: number;
    meta_data: number;
    ad_copy: number;
    newsletters: number;
    calendars: number;
    keyword_research: number;
    faq_blocks?: number;
    content_blocks?: number;
    multichannel_carousel: number;
    multichannel_video_script: number;
    multichannel_pull_quotes: number;
    edm_templates: number;
    edm_sequences: number;
  };
  streams: StreamNode[];
  quick_wins: {
    social_media: QuickWinGroup[];
    meta_data: QuickWinGroup[];
    ad_copy: QuickWinGroup[];
    email_newsletter: QuickWinGroup[];
    content_calendar: QuickWinGroup[];
    keyword_research: QuickWinGroup[];
    faq_block?: QuickWinGroup[];
    content_block?: QuickWinGroup[];
    multichannel_carousel: QuickWinGroup[];
    multichannel_video_script: QuickWinGroup[];
    multichannel_pull_quotes: QuickWinGroup[];
  };
  edm?: {
    templates: EDMTemplateNode[];
    sequences: EDMSequenceNode[];
  };
}

interface CollapsibleSectionProps {
  title: React.ReactNode;
  icon: React.ReactNode;
  count?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
  level?: number;
  /** Optional per-section header action (e.g. a rename pencil), rendered
   * after the count badge. Its own click handler must stopPropagation —
   * .section-header's onClick toggles open/closed. */
  headerAction?: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  icon,
  count,
  defaultOpen = false,
  children,
  level = 0,
  headerAction,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={`catalogue-section level-${level}`}>
      <div
        className="section-header"
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => e.key === 'Enter' && setIsOpen(!isOpen)}
      >
        <span className="expand-icon">
          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
        <span className="section-icon">{icon}</span>
        <span className="section-title">{title}</span>
        {count !== undefined && count > 0 && (
          <span className="count-badge">{count}</span>
        )}
        {headerAction && (
          <span className="section-header-action" onClick={(e) => e.stopPropagation()}>
            {headerAction}
          </span>
        )}
      </div>

      {isOpen && (
        <div className="section-content">
          {children}
        </div>
      )}
    </div>
  );
};

interface ContentItemProps {
  taskId: string;
  title: string;
  status: string;
  hasImage?: boolean;
  thumbnailUrl?: string | null;
  quickWins?: string[];
  approvalStatus?: string | null;
  onClick: (taskId: string) => void;
  onDelete?: (taskId: string) => void;
  onSchedule?: (taskId: string) => void;
  onMove?: (taskId: string) => void;
  onWhitepaper?: (taskId: string) => void;
  /** Optional project ID — enables CatalogueLinkBadges when provided */
  linkBadgeProjectId?: string | null;
  /** pending_link_opportunities dict from project.client_info */
  linkBadgePendingOpp?: Record<string, any[]>;
}

const ContentItem: React.FC<ContentItemProps> = ({
  taskId,
  title,
  status,
  hasImage = false,
  thumbnailUrl,
  quickWins = [],
  approvalStatus,
  onClick,
  onDelete,
  onSchedule,
  onMove,
  onWhitepaper,
  linkBadgeProjectId,
  linkBadgePendingOpp,
}) => {
  const getStatusIcon = () => {
    switch (status) {
      case 'completed': return <Check size={12} />;
      case 'in_progress': return <Clock size={12} />;
      case 'failed': return <XCircle size={12} />;
      default: return null;
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(taskId);
    }
  };

  const handleSchedule = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSchedule) {
      onSchedule(taskId);
    }
  };

  const handleMove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onMove) {
      onMove(taskId);
    }
  };

  const handleWhitepaper = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onWhitepaper) {
      onWhitepaper(taskId);
    }
  };

  return (
    <div
      className={`content-item status-${status}`}
      onClick={() => onClick(taskId)}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => e.key === 'Enter' && onClick(taskId)}
    >
      {thumbnailUrl && (
        <img
          src={thumbnailUrl}
          alt=""
          loading="lazy"
          className="item-thumbnail"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />
      )}
      <span className="item-title">{title}</span>
      <div className="item-badges">
        {hasImage && <span className="badge image-badge" title="Has hero image"><ImageIcon size={12} /></span>}
        {quickWins.length > 0 && (
          <span className="badge quickwin-badge" title={`${quickWins.length} quick wins`}>
            <BarChart3 size={12} />
          </span>
        )}
        <span className={`badge status-badge status-${status}`} title={status}>
          {getStatusIcon()}
        </span>
        {approvalStatus && (
          <span className={`badge approval-indicator approval-${approvalStatus}`} title={`Approval: ${approvalStatus}`}>
            {approvalStatus === 'approved' ? <Check size={12} /> : approvalStatus === 'rejected' ? <XCircle size={12} /> : <Clock size={12} />}
          </span>
        )}
        {onMove && (
          <button
            className="badge move-badge"
            title="Move to Stream"
            onClick={handleMove}
            tabIndex={0}
          >
            <ArrowRightLeft size={12} />
          </button>
        )}
        {onSchedule && status === 'completed' && (
          <button
            className="badge schedule-badge"
            title="Schedule Publication"
            onClick={handleSchedule}
            tabIndex={0}
          >
            <Calendar size={12} />
          </button>
        )}
        {onWhitepaper && status === 'completed' && (
          <button
            className="badge whitepaper-action-badge"
            title="Create Whitepaper"
            onClick={handleWhitepaper}
            tabIndex={0}
          >
            <FileText size={12} />
          </button>
        )}
        {onDelete && (
          <button
            className="badge delete-badge"
            title="Delete"
            onClick={handleDelete}
            tabIndex={0}
          >
            <Trash2 size={12} />
          </button>
        )}
        {linkBadgeProjectId && (
          <CatalogueLinkBadges
            taskId={taskId}
            projectId={linkBadgeProjectId}
            pendingOpportunities={linkBadgePendingOpp}
          />
        )}
      </div>
    </div>
  );
};

type CatalogueTabKey = 'overview' | 'articles' | 'content_calendar' | 'quick_wins' | 'edm' | 'masterplan';
type QWSubTab = 'multichannel' | 'newsletters' | 'seo' | 'ads' | 'social' | 'keywords' | 'faqs' | 'blocks';
const VALID_QW_SUBTABS: QWSubTab[] = ['multichannel', 'newsletters', 'seo', 'ads', 'social', 'keywords', 'faqs', 'blocks'];
type MCSubTab = 'carousels' | 'video_scripts' | 'pull_quotes';

interface ContentCatalogueProps {
  projectId: string | null;
  onClose?: () => void;
  fullpage?: boolean;
  refreshKey?: number;
}

interface QuickWinItem {
  task_id: string;
  title: string;
  source_article?: string | null;
  source_task_id?: string | null;
  source_hero_image_url?: string | null;
  approval_status?: string | null;
}

interface StreamData {
  name: string;
  article_count: number;
  articles: Array<{ task_id: string; title: string; status: string; has_image?: boolean; quick_wins?: string[]; approval_status?: string | null }>;
}

/** Groups quick-win items (social posts, SEO metadata, etc) by their source
 *  article's stream, then renders one CollapsibleSection per stream. Items
 *  whose source article can't be found go under "Unassigned". */
const StreamGroupedQuickWins: React.FC<{
  items: QuickWinItem[];
  streams: StreamData[];
  sectionTitle: string;
  sectionIcon: React.ReactNode;
  onClick: (taskId: string) => void;
  onDelete?: (taskId: string) => void;
}> = ({ items, streams, sectionTitle, sectionIcon, onClick, onDelete }) => {
  // Build task_id → stream_name lookup from the streams tree
  const streamByArticleId = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const s of streams) {
      for (const a of s.articles) {
        map.set(a.task_id, s.name);
      }
    }
    return map;
  }, [streams]);

  // Group items by stream
  const byStream = React.useMemo(() => {
    const groups = new Map<string, QuickWinItem[]>();
    for (const it of items) {
      const streamName = (it.source_task_id && streamByArticleId.get(it.source_task_id)) || 'Unassigned';
      if (!groups.has(streamName)) groups.set(streamName, []);
      groups.get(streamName)!.push(it);
    }
    // Sort: streams in the order they appear in contentTree.streams, then Unassigned last
    const streamOrder = streams.map(s => s.name);
    return Array.from(groups.entries()).sort(([a], [b]) => {
      const aIdx = streamOrder.indexOf(a);
      const bIdx = streamOrder.indexOf(b);
      if (a === 'Unassigned') return 1;
      if (b === 'Unassigned') return -1;
      if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
      if (aIdx === -1) return 1;
      if (bIdx === -1) return -1;
      return aIdx - bIdx;
    });
  }, [items, streamByArticleId, streams]);

  return (
    <CollapsibleSection
      title={sectionTitle}
      icon={sectionIcon}
      count={items.length}
      defaultOpen={true}
      level={0}
    >
      {byStream.map(([streamName, streamItems]) => (
        <CollapsibleSection
          key={streamName}
          title={streamName}
          icon={<FileText size={14} />}
          count={streamItems.length}
          defaultOpen={false}
          level={1}
        >
          {streamItems.map(item => (
            <ContentItem
              key={item.task_id}
              taskId={item.task_id}
              title={item.source_article || item.title}
              status="completed"
              thumbnailUrl={item.source_hero_image_url}
              approvalStatus={item.approval_status ?? undefined}
              onClick={onClick}
              onDelete={onDelete}
            />
          ))}
        </CollapsibleSection>
      ))}
    </CollapsibleSection>
  );
};

const ContentCatalogue: React.FC<ContentCatalogueProps> = ({ projectId, onClose, fullpage, refreshKey }) => {
  const navigate = useNavigate();
  const [contentTree, setContentTree] = useState<ContentTreeData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [selectedTaskForSchedule, setSelectedTaskForSchedule] = useState<string | null>(null);
  const [reassigningTaskId, setReassigningTaskId] = useState<string | null>(null);
  const [creatingStream, setCreatingStream] = useState(false);
  const [newStreamName, setNewStreamName] = useState('');
  const [creatingStreamForTaskId, setCreatingStreamForTaskId] = useState<string | null>(null);
  const [newStreamNameForTask, setNewStreamNameForTask] = useState('');

  // Stream rename — inline edit + preview/confirm flow
  const [renamingStream, setRenamingStream] = useState<string | null>(null);
  // Set true only once a preview has actually been requested (inside
  // handleSubmitRenameDraft) — distinct from `renamingStream`, which is set
  // the instant the pencil is clicked. Gating the confirm modal on this
  // (rather than on `renamingStream`) is what keeps the modal's full-screen
  // overlay from covering the inline input before the user has typed
  // anything or pressed Enter.
  const [renamePreviewOpen, setRenamePreviewOpen] = useState(false);
  const [renamePreview, setRenamePreview] = useState<StreamRenameResult | null>(null);
  const [renameDraftDisplayName, setRenameDraftDisplayName] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);

  // Tab state — persisted in URL via ?tab= for deep-linking
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const initialTab = (urlParams?.get('tab') as CatalogueTabKey) || 'overview';
  const initialQwSubTab: QWSubTab = (() => {
    const raw = urlParams?.get('qw');
    return raw && (VALID_QW_SUBTABS as readonly string[]).includes(raw) ? (raw as QWSubTab) : 'multichannel';
  })();
  const [activeTab, setActiveTab] = useState<CatalogueTabKey>(initialTab);
  const [qwSubTab, setQwSubTab] = useState<QWSubTab>(initialQwSubTab);
  const [mcSubTab, setMcSubTab] = useState<MCSubTab>('carousels');

  const setTab = (tab: CatalogueTabKey) => {
    setActiveTab(tab);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (tab === 'overview') url.searchParams.delete('tab');
      else url.searchParams.set('tab', tab);
      window.history.replaceState(null, '', url.toString());
    }
  };

  const setQwSubTabSynced = (sub: QWSubTab) => {
    setQwSubTab(sub);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (sub === 'multichannel') url.searchParams.delete('qw');
      else url.searchParams.set('qw', sub);
      window.history.replaceState(null, '', url.toString());
    }
  };

  useEffect(() => {
    if (!projectId) {
      setContentTree(null);
      return;
    }

    loadContentTree();
  }, [projectId]);

  // Refresh when parent signals a data change
  useEffect(() => {
    if (refreshKey !== undefined && refreshKey > 0 && projectId) {
      loadContentTree();
    }
  }, [refreshKey]);

  const loadContentTree = async () => {
    if (!projectId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getContentTreeForProject(projectId);
      setContentTree(data);
    } catch (err: any) {
      console.error('Failed to load content tree:', err);
      setError('Failed to load content catalogue');
    } finally {
      setLoading(false);
    }
  };

  const openContentInNewTab = (taskId: string) => {
    // Open content preview in new tab
    const url = `${window.location.origin}/preview/${taskId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteTask(taskId);
      // Refresh the content tree after deletion
      await loadContentTree();
    } catch (err: any) {
      console.error('Failed to delete task:', err);
      alert('Failed to delete item. Please try again.');
    }
  };

  const handleScheduleTask = (taskId: string) => {
    setSelectedTaskForSchedule(taskId);
    setScheduleModalOpen(true);
  };

  const handleScheduleComplete = () => {
    setScheduleModalOpen(false);
    setSelectedTaskForSchedule(null);
  };

  const handleMoveTask = (taskId: string) => {
    setReassigningTaskId(prev => prev === taskId ? null : taskId);
  };

  const handleWhitepaperTask = (taskId: string) => {
    const params = new URLSearchParams({ whitepaper_from: taskId });
    if (projectId) params.set('whitepaper_project', projectId);
    navigate(`/products?${params.toString()}`);
  };

  const handleStreamSelect = async (taskId: string, stream: string) => {
    try {
      await reassignTaskStream(taskId, stream);
      setReassigningTaskId(null);
      await loadContentTree();
    } catch (err: any) {
      console.error('Failed to reassign stream:', err);
      alert('Failed to move article. Please try again.');
    }
  };

  const handleStartCreateNewStreamForTask = (taskId: string) => {
    setCreatingStreamForTaskId(taskId);
    setNewStreamNameForTask('');
  };

  const handleConfirmCreateNewStreamForTask = async () => {
    const taskId = creatingStreamForTaskId;
    const name = newStreamNameForTask.trim();
    if (!name || !taskId || !projectId) return;
    try {
      await createStream(projectId, name);
      await reassignTaskStream(taskId, name);
      setCreatingStreamForTaskId(null);
      setNewStreamNameForTask('');
      setReassigningTaskId(null);
      await loadContentTree();
    } catch (err: any) {
      console.error('Failed to create stream:', err);
      alert(getApiError(err, 'Failed to create stream.'));
    }
  };

  const handleCreateStream = async () => {
    if (!newStreamName.trim() || !projectId) return;
    try {
      await createStream(projectId, newStreamName.trim());
      setNewStreamName('');
      setCreatingStream(false);
      await loadContentTree();
    } catch (err: any) {
      console.error('Failed to create stream:', err);
      alert(getApiError(err, 'Failed to create stream.'));
    }
  };

  // --- Stream rename ---

  const handleStartRenameStream = (streamName: string) => {
    setRenamingStream(streamName);
  };

  const handleCancelRenameStream = () => {
    setRenamingStream(null);
  };

  const handleSubmitRenameDraft = async (draftDisplayName: string) => {
    if (!renamingStream || !projectId) return;
    setRenameDraftDisplayName(draftDisplayName);
    setRenamePreview(null);
    // Only now — once a preview has actually been requested — does the
    // confirm modal become eligible to render. Before this point the pencil
    // click has only opened the inline StreamRenameRow input.
    setRenamePreviewOpen(true);
    try {
      const preview = await previewStreamRename(projectId, renamingStream, draftDisplayName);
      setRenamePreview(preview);
    } catch (err: any) {
      console.error('Failed to preview stream rename:', err);
      setRenamingStream(null);
      setRenamePreviewOpen(false);
      alert(getApiError(err, 'Failed to preview stream rename.'));
    }
  };

  const handleConfirmRenameStream = async () => {
    if (!renamingStream || !projectId) return;
    setRenameBusy(true);
    try {
      await renameStreamApi(projectId, renamingStream, renameDraftDisplayName);
      setRenamingStream(null);
      setRenamePreview(null);
      setRenamePreviewOpen(false);
      await loadContentTree();
    } catch (err: any) {
      console.error('Failed to rename stream:', err);
      alert(getApiError(err, 'Failed to rename stream.'));
    } finally {
      setRenameBusy(false);
    }
  };

  const handleCancelRenamePreview = () => {
    setRenamePreview(null);
    setRenamePreviewOpen(false);
    setRenamingStream(null);
  };

  const allStreamNames = contentTree?.streams.map(s => s.name) || [];

  const rootClass = `content-catalogue${fullpage ? ' fullpage' : ''}`;

  if (!projectId) {
    return (
      <div className={`${rootClass} empty`}>
        <div className="catalogue-header">
          <h2><BookOpen size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} /> Content Catalogue</h2>
        </div>
        <div className="empty-state">
          <p>Select a project to view content</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`${rootClass} loading`}>
        <div className="catalogue-header">
          <h2><BookOpen size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} /> Content Catalogue</h2>
        </div>
        <div className="loading-state">
          <p>Loading content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${rootClass} error`}>
        <div className="catalogue-header">
          <h2><BookOpen size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} /> Content Catalogue</h2>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={loadContentTree}>Retry</button>
        </div>
      </div>
    );
  }

  if (!contentTree) {
    return null;
  }

  return (
    <LinkGraphCacheProvider projectId={projectId}>
    <div className={rootClass}>
      {scheduleModalOpen && selectedTaskForSchedule && projectId && (
        <SchedulePublicationModal
          isOpen={scheduleModalOpen}
          onClose={() => setScheduleModalOpen(false)}
          taskId={selectedTaskForSchedule}
          projectId={projectId}
          onScheduled={handleScheduleComplete}
        />
      )}

      <div className="catalogue-header">
        <h2><BookOpen size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} /> Content Catalogue</h2>
        <div className="catalogue-header-actions">
          <button
            className="refresh-btn"
            onClick={loadContentTree}
            title="Refresh catalogue"
          >
            <RefreshCw size={16} />
          </button>
          {onClose && !fullpage && (
            <button
              className="close-btn mobile-only"
              onClick={onClose}
              title="Close catalogue"
              aria-label="Close catalogue"
            >
              <X size={20} />
            </button>
          )}
        </div>
      </div>

      {/* Level 1 tab bar */}
      <div className="catalogue-tabs" role="tablist">
        {(() => {
          const ov = contentTree.overview;
          const qw = contentTree.quick_wins;
          const calCount = qw.content_calendar.length;
          const multiCount = (qw.multichannel_carousel?.length || 0) + (qw.multichannel_video_script?.length || 0) + (qw.multichannel_pull_quotes?.length || 0);
          const qwCount = qw.social_media.length + qw.meta_data.length + qw.ad_copy.length + (qw.email_newsletter?.length || 0) + (qw.keyword_research?.length || 0) + (qw.faq_block?.length || 0) + (qw.content_block?.length || 0) + multiCount;
          const edmCount = contentTree.edm ? (contentTree.edm.templates.length + contentTree.edm.sequences.length) : 0;

          const tabs: Array<{ key: CatalogueTabKey; label: string; count: number; alwaysShow?: boolean }> = [
            { key: 'overview',          label: 'Overview',         count: 0,        alwaysShow: true },
            { key: 'articles',          label: 'Articles',         count: ov.articles, alwaysShow: true },
            { key: 'content_calendar',  label: 'Content Calendar', count: calCount },
            { key: 'quick_wins',        label: 'Quick Wins',       count: qwCount },
            { key: 'edm',              label: 'Email Marketing',   count: edmCount },
            { key: 'masterplan',       label: 'Masterplan',        count: 0,        alwaysShow: true },
          ];
          return tabs
            .filter(t => t.alwaysShow || t.count > 0)
            .map(t => (
              <button key={t.key} role="tab" aria-selected={activeTab === t.key}
                className={`catalogue-tab${activeTab === t.key ? ' active' : ''}`}
                onClick={() => setTab(t.key)}>
                {t.label}
                {t.count > 0 && <span className="catalogue-tab-count">{t.count}</span>}
              </button>
            ));
        })()}
      </div>

      {/* Level 2 sub-tabs (Quick Wins only) */}
      {activeTab === 'quick_wins' && (() => {
        const qw = contentTree.quick_wins;
        const multiCount = (qw.multichannel_carousel?.length || 0) + (qw.multichannel_video_script?.length || 0) + (qw.multichannel_pull_quotes?.length || 0);
        const subTabs = ([
          { key: 'multichannel' as QWSubTab, label: 'Multi Channel', count: multiCount },
          { key: 'newsletters'  as QWSubTab, label: 'Emails & Newsletters', count: qw.email_newsletter?.length || 0 },
          { key: 'seo'          as QWSubTab, label: 'SEO',           count: qw.meta_data.length },
          { key: 'ads'          as QWSubTab, label: 'Ad Copy',       count: qw.ad_copy.length },
          { key: 'social'       as QWSubTab, label: 'Social Media',  count: qw.social_media.length },
          { key: 'keywords'     as QWSubTab, label: 'Keywords',      count: qw.keyword_research?.length || 0 },
          { key: 'faqs'         as QWSubTab, label: 'FAQs',          count: qw.faq_block?.length || 0 },
          { key: 'blocks'       as QWSubTab, label: 'Content Blocks', count: qw.content_block?.length || 0 },
        ]).filter(t => t.count > 0);
        if (subTabs.length === 0) return null;
        // Ensure active sub-tab is valid
        const validKeys = subTabs.map(t => t.key);
        const effectiveQwTab = validKeys.includes(qwSubTab) ? qwSubTab : validKeys[0];
        return (
          <div className="catalogue-subtabs" role="tablist">
            {subTabs.map(t => (
              <button key={t.key} role="tab" aria-selected={effectiveQwTab === t.key}
                className={`catalogue-subtab${effectiveQwTab === t.key ? ' active' : ''}`}
                onClick={() => setQwSubTabSynced(t.key)}>
                {t.label}
                <span className="catalogue-subtab-count">{t.count}</span>
              </button>
            ))}
          </div>
        );
      })()}

      {/* Level 3 sub-tabs (Multi Channel only) */}
      {activeTab === 'quick_wins' && qwSubTab === 'multichannel' && (() => {
        const qw = contentTree.quick_wins;
        const l3Tabs = ([
          { key: 'carousels'     as MCSubTab, label: 'Carousels',     count: qw.multichannel_carousel?.length || 0 },
          { key: 'video_scripts' as MCSubTab, label: 'Video Scripts', count: qw.multichannel_video_script?.length || 0 },
          { key: 'pull_quotes'   as MCSubTab, label: 'Pull Quotes',   count: qw.multichannel_pull_quotes?.length || 0 },
        ]).filter(t => t.count > 0);
        if (l3Tabs.length <= 1) return null;
        const validMC = l3Tabs.map(t => t.key);
        const effectiveMC = validMC.includes(mcSubTab) ? mcSubTab : validMC[0];
        return (
          <div className="catalogue-l3tabs" role="tablist">
            {l3Tabs.map(t => (
              <button key={t.key} role="tab" aria-selected={effectiveMC === t.key}
                className={`catalogue-l3tab${effectiveMC === t.key ? ' active' : ''}`}
                onClick={() => setMcSubTab(t.key)}>
                {t.label}
                <span className="catalogue-l3tab-count">{t.count}</span>
              </button>
            ))}
          </div>
        );
      })()}

      <div className="catalogue-body">
        {/* Overview Section */}
        {activeTab === 'overview' && (
        <div className="overview-section">
          <h3><BarChart3 size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} /> Overview</h3>
          <div className="overview-stats">
            <div className="stat-item">
              <span className="stat-label">Articles:</span>
              <span className="stat-value">{contentTree.overview.articles}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Social Media:</span>
              <span className="stat-value">{contentTree.overview.social_media}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">SEO Metadata:</span>
              <span className="stat-value">{contentTree.overview.meta_data}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Ad Copy:</span>
              <span className="stat-value">{contentTree.overview.ad_copy}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Emails &amp; Newsletters:</span>
              <span className="stat-value">{contentTree.overview.newsletters}</span>
            </div>
            {(contentTree.overview.multichannel_carousel + contentTree.overview.multichannel_video_script + contentTree.overview.multichannel_pull_quotes) > 0 && (
              <div className="stat-item">
                <span className="stat-label">Multi Channel:</span>
                <span className="stat-value">{contentTree.overview.multichannel_carousel + contentTree.overview.multichannel_video_script + contentTree.overview.multichannel_pull_quotes}</span>
              </div>
            )}
            {(contentTree.overview.edm_templates + contentTree.overview.edm_sequences) > 0 && (
              <div className="stat-item">
                <span className="stat-label">Email Marketing:</span>
                <span className="stat-value">{contentTree.overview.edm_templates + contentTree.overview.edm_sequences}</span>
              </div>
            )}
            {(contentTree.overview.faq_blocks ?? 0) > 0 && (
              <div className="stat-item">
                <span className="stat-label">FAQs:</span>
                <span className="stat-value">{contentTree.overview.faq_blocks}</span>
              </div>
            )}
            {(contentTree.overview.content_blocks ?? 0) > 0 && (
              <div className="stat-item">
                <span className="stat-label">Content Blocks:</span>
                <span className="stat-value">{contentTree.overview.content_blocks}</span>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Articles by Stream — with Generate menubar */}
        {activeTab === 'articles' && (
          <CatalogueMenuBar
            projectId={projectId}
            streamNames={contentTree.streams.map(s => s.name)}
            onComplete={loadContentTree}
          />
        )}
        {activeTab === 'articles' && contentTree.streams.length > 0 && (
          <CollapsibleSection
            title="Articles by Stream"
            icon={<FileText size={16} />}
            count={contentTree.overview.articles}
            defaultOpen={true}
            level={0}
          >
            <div className="stream-section-actions">
              {creatingStream ? (
                <div className="new-stream-input-row">
                  <input
                    className="new-stream-input"
                    type="text"
                    placeholder="Stream name..."
                    value={newStreamName}
                    onChange={e => setNewStreamName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleCreateStream()}
                    autoFocus
                  />
                  <button className="new-stream-confirm" onClick={handleCreateStream} title="Create">
                    <Check size={14} />
                  </button>
                  <button className="new-stream-cancel" onClick={() => { setCreatingStream(false); setNewStreamName(''); }} title="Cancel">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button className="new-stream-btn" onClick={() => setCreatingStream(true)}>
                  <Plus size={14} /> New Stream
                </button>
              )}
            </div>
            {contentTree.streams.map((stream) => (
              <CollapsibleSection
                key={stream.name}
                title={
                  renamingStream === stream.name && !renamePreviewOpen ? (
                    <StreamRenameRow
                      initialValue={stripStreamPrefix(stream.name)}
                      onConfirm={handleSubmitRenameDraft}
                      onCancel={handleCancelRenameStream}
                    />
                  ) : (
                    stream.name
                  )
                }
                icon={<FileText size={14} />}
                count={stream.article_count}
                defaultOpen={false}
                level={1}
                headerAction={
                  renamingStream === stream.name ? undefined : (
                    <button
                      className="section-header-action-btn"
                      onClick={() => handleStartRenameStream(stream.name)}
                      title="Rename stream"
                    >
                      <Pencil size={12} />
                    </button>
                  )
                }
              >
                {stream.articles.map((article) => (
                  <React.Fragment key={article.task_id}>
                    <ContentItem
                      taskId={article.task_id}
                      title={article.title}
                      status={article.status}
                      hasImage={article.has_image}
                      quickWins={article.quick_wins}
                      approvalStatus={article.approval_status}
                      onClick={openContentInNewTab}
                      onDelete={article.status !== 'pending' ? handleDeleteTask : undefined}
                      onSchedule={handleScheduleTask}
                      onMove={handleMoveTask}
                      onWhitepaper={handleWhitepaperTask}
                      linkBadgeProjectId={projectId}
                    />
                    {reassigningTaskId === article.task_id && (
                      <div className="stream-picker" onClick={e => e.stopPropagation()}>
                        <div className="stream-picker-label">Move to stream:</div>
                        {allStreamNames.filter(s => s !== stream.name).map(s => (
                          <button
                            key={s}
                            className="stream-picker-option"
                            onClick={() => handleStreamSelect(article.task_id, s)}
                          >
                            {s}
                          </button>
                        ))}
                        {creatingStreamForTaskId === article.task_id ? (
                          <div className="new-stream-input-row">
                            <input
                              className="new-stream-input"
                              type="text"
                              placeholder="Stream name..."
                              value={newStreamNameForTask}
                              onChange={e => setNewStreamNameForTask(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && handleConfirmCreateNewStreamForTask()}
                              autoFocus
                            />
                            <button className="new-stream-confirm" onClick={handleConfirmCreateNewStreamForTask} title="Create">
                              <Check size={14} />
                            </button>
                            <button className="new-stream-cancel" onClick={() => setCreatingStreamForTaskId(null)} title="Cancel">
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <button
                            className="stream-picker-option stream-picker-new"
                            onClick={() => handleStartCreateNewStreamForTask(article.task_id)}
                          >
                            <Plus size={12} /> New Stream...
                          </button>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </CollapsibleSection>
            ))}
          </CollapsibleSection>
        )}
        {renamePreviewOpen && (
          <StreamRenameConfirmModal
            displayName={renameDraftDisplayName}
            preview={renamePreview}
            busy={renameBusy}
            onConfirm={handleConfirmRenameStream}
            onCancel={handleCancelRenamePreview}
          />
        )}

        {/* Content Calendar (Level 1 tab) */}
        {activeTab === 'content_calendar' && contentTree.quick_wins.content_calendar.length > 0 && (
          <CollapsibleSection title="Content Calendars" icon={<CalendarDays size={16} />}
            count={contentTree.quick_wins.content_calendar.length} defaultOpen={true} level={0}>
            {contentTree.quick_wins.content_calendar.map(item => (
              <ContentItem key={item.task_id} taskId={item.task_id} title={item.title}
                status="completed" approvalStatus={item.approval_status}
                onClick={openContentInNewTab} onDelete={handleDeleteTask} />
            ))}
          </CollapsibleSection>
        )}

        {/* Quick Wins — Multi Channel (L2) */}
        {activeTab === 'quick_wins' && qwSubTab === 'multichannel' && (() => {
          const qw = contentTree.quick_wins;
          const validMC = (['carousels', 'video_scripts', 'pull_quotes'] as MCSubTab[]).filter(k =>
            k === 'carousels' ? (qw.multichannel_carousel?.length || 0) > 0 :
            k === 'video_scripts' ? (qw.multichannel_video_script?.length || 0) > 0 :
            (qw.multichannel_pull_quotes?.length || 0) > 0
          );
          const effectiveMC = validMC.includes(mcSubTab) ? mcSubTab : (validMC[0] || 'carousels');
          return (
            <>
              {effectiveMC === 'carousels' && (qw.multichannel_carousel?.length || 0) > 0 && (
                <CollapsibleSection title="Carousels" icon={<Layers size={16} />}
                  count={qw.multichannel_carousel!.length} defaultOpen={true} level={0}>
                  {qw.multichannel_carousel!.map(item => (
                    <ContentItem key={item.task_id} taskId={item.task_id} title={item.source_article || item.title}
                      status="completed" thumbnailUrl={item.source_hero_image_url} approvalStatus={item.approval_status}
                      onClick={openContentInNewTab} onDelete={handleDeleteTask} />
                  ))}
                </CollapsibleSection>
              )}
              {effectiveMC === 'video_scripts' && (qw.multichannel_video_script?.length || 0) > 0 && (
                <CollapsibleSection title="Video Scripts" icon={<Video size={16} />}
                  count={qw.multichannel_video_script!.length} defaultOpen={true} level={0}>
                  {qw.multichannel_video_script!.map(item => (
                    <ContentItem key={item.task_id} taskId={item.task_id} title={item.source_article || item.title}
                      status="completed" thumbnailUrl={item.source_hero_image_url} approvalStatus={item.approval_status}
                      onClick={openContentInNewTab} onDelete={handleDeleteTask} />
                  ))}
                </CollapsibleSection>
              )}
              {effectiveMC === 'pull_quotes' && (qw.multichannel_pull_quotes?.length || 0) > 0 && (
                <CollapsibleSection title="Pull Quotes" icon={<Quote size={16} />}
                  count={qw.multichannel_pull_quotes!.length} defaultOpen={true} level={0}>
                  {qw.multichannel_pull_quotes!.map(item => (
                    <ContentItem key={item.task_id} taskId={item.task_id} title={item.source_article || item.title}
                      status="completed" thumbnailUrl={item.source_hero_image_url} approvalStatus={item.approval_status}
                      onClick={openContentInNewTab} onDelete={handleDeleteTask} />
                  ))}
                </CollapsibleSection>
              )}
            </>
          );
        })()}

        {/* Quick Wins — Newsletters (L2) */}
        {activeTab === 'quick_wins' && qwSubTab === 'newsletters' && (contentTree.quick_wins.email_newsletter?.length || 0) > 0 && (
          <CollapsibleSection title="Emails & Newsletters" icon={<Mail size={16} />}
            count={contentTree.quick_wins.email_newsletter.length} defaultOpen={true} level={0}>
            {contentTree.quick_wins.email_newsletter.map(item => (
              <ContentItem key={item.task_id} taskId={item.task_id} title={item.title}
                status="completed" approvalStatus={item.approval_status}
                onClick={openContentInNewTab} onDelete={handleDeleteTask} />
            ))}
          </CollapsibleSection>
        )}

        {/* Quick Wins — SEO (L2) */}
        {activeTab === 'quick_wins' && qwSubTab === 'seo' && contentTree.quick_wins.meta_data.length > 0 && (
          <StreamGroupedQuickWins items={contentTree.quick_wins.meta_data} streams={contentTree.streams}
            sectionTitle="SEO Metadata" sectionIcon={<Search size={16} />}
            onClick={openContentInNewTab} onDelete={handleDeleteTask} />
        )}

        {/* Quick Wins — Ad Copy (L2) */}
        {activeTab === 'quick_wins' && qwSubTab === 'ads' && contentTree.quick_wins.ad_copy.length > 0 && (
          <CollapsibleSection title="Ad Copy" icon={<Megaphone size={16} />}
            count={contentTree.quick_wins.ad_copy.length} defaultOpen={true} level={0}>
            {contentTree.quick_wins.ad_copy.map(item => (
              <ContentItem key={item.task_id} taskId={item.task_id} title={item.source_article || item.title}
                status="completed" approvalStatus={item.approval_status}
                onClick={openContentInNewTab} onDelete={handleDeleteTask} />
            ))}
          </CollapsibleSection>
        )}

        {/* Quick Wins — Social Media (L2) */}
        {activeTab === 'quick_wins' && qwSubTab === 'social' && contentTree.quick_wins.social_media.length > 0 && (
          <StreamGroupedQuickWins items={contentTree.quick_wins.social_media} streams={contentTree.streams}
            sectionTitle="Social Media Posts" sectionIcon={<Share2 size={16} />}
            onClick={openContentInNewTab} onDelete={handleDeleteTask} />
        )}

        {/* Quick Wins — Keywords (L2) */}
        {activeTab === 'quick_wins' && qwSubTab === 'keywords' && (contentTree.quick_wins.keyword_research?.length || 0) > 0 && (
          <CollapsibleSection title="Keyword Research" icon={<Key size={16} />}
            count={contentTree.quick_wins.keyword_research!.length} defaultOpen={true} level={0}>
            {contentTree.quick_wins.keyword_research!.map(item => (
              <ContentItem key={item.task_id} taskId={item.task_id} title={item.source_article || item.title}
                status="completed" approvalStatus={item.approval_status}
                onClick={openContentInNewTab} onDelete={handleDeleteTask} />
            ))}
          </CollapsibleSection>
        )}

        {/* Quick Wins — FAQs (L2) */}
        {activeTab === 'quick_wins' && qwSubTab === 'faqs' && (contentTree.quick_wins.faq_block?.length || 0) > 0 && (
          <CollapsibleSection title="FAQs" icon={<HelpCircle size={16} />}
            count={contentTree.quick_wins.faq_block!.length} defaultOpen={true} level={0}>
            {contentTree.quick_wins.faq_block!.map(item => (
              <ContentItem key={item.task_id} taskId={item.task_id} title={item.title}
                status="completed" approvalStatus={item.approval_status}
                onClick={openContentInNewTab} onDelete={handleDeleteTask} />
            ))}
          </CollapsibleSection>
        )}

        {/* Quick Wins — Content Blocks (L2) */}
        {activeTab === 'quick_wins' && qwSubTab === 'blocks' && (contentTree.quick_wins.content_block?.length || 0) > 0 && (
          <CollapsibleSection title="Content Blocks" icon={<Layers size={16} />}
            count={contentTree.quick_wins.content_block!.length} defaultOpen={true} level={0}>
            {contentTree.quick_wins.content_block!.map(item => (
              <ContentItem key={item.task_id} taskId={item.task_id} title={item.title}
                status="completed" approvalStatus={item.approval_status}
                onClick={openContentInNewTab} onDelete={handleDeleteTask} />
            ))}
          </CollapsibleSection>
        )}

        {/* Email Marketing — Sequences only (templates managed in /edm) */}
        {activeTab === 'edm' && contentTree.edm && contentTree.edm.sequences.length > 0 && (
          <CollapsibleSection
            title="Email Sequences"
            icon={<Send size={16} />}
            count={contentTree.edm.sequences.length}
            level={0}
            defaultOpen={true}
          >
            {contentTree.edm.sequences.map((seq) => (
              <div
                key={seq.sequence_id}
                className="catalogue-item"
                onClick={() => window.open(`/edm?tab=sequences&sequenceId=${seq.sequence_id}`, '_blank')}
              >
                <span className="catalogue-item-title">{seq.name}</span>
                <span className="catalogue-item-badges">
                  <span className={`catalogue-badge status-${seq.status}`}>{seq.status}</span>
                  <span className="catalogue-badge">{seq.step_count} steps</span>
                </span>
              </div>
            ))}
          </CollapsibleSection>
        )}

        {/* Masterplan Section */}
        {activeTab === 'masterplan' && (
          <MasterplanPanel projectId={projectId} onGenerated={loadContentTree} />
        )}
      </div>

      {/* Footer Actions */}
      <div className="catalogue-footer">
        <button
          className="export-all-btn"
          onClick={async () => {
            if (!projectId) return;
            try {
              const blob = await exportProjectArticles(projectId, 'completed', 'zip');
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `articles-${projectId}.zip`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
            } catch (err) {
              console.error('Export All failed:', err);
            }
          }}
        >
          Export All
        </button>
      </div>
    </div>
    </LinkGraphCacheProvider>
  );
};

export default ContentCatalogue;
